import ErrorBoundary from '@/components/ErrorBoundary';
import { StandaloneArticleHeaderProps, findBylines } from '@/components/content/ArticleHeader';
import { ArticleHeader } from '@/components/content/ArticleHeader/ArticleHeader';
import { BYLINE_TYPE, StandaloneBylineProps } from '@/components/content/Byline';
import { StandaloneImageProps } from '@/components/content/Image';
import { StandaloneArticleDateProps } from '@/components/element/ArticleDate';
import { StandaloneSaveArticleButtonProps } from '@/components/element/SaveArticleButton';
import { StandaloneTagProps } from '@/components/element/Tag';
import { useStable, useStableProps } from '@/hooks/useStable';
import { componentTheme, ClassNameProvider } from '@/styles/ContentArticleHeader';
import { DefaultComponent, StandaloneComponent } from '@/types/component';
import { mergeOptions, mergeProps } from '@/utils/merge';
import { useAppState } from 'lib/hooks/useAppState';
import { standaloneComponent } from 'modules/dynamic-components';
import React, { useMemo } from 'react';

const ArticleDate = standaloneComponent<StandaloneArticleDateProps>('ElementArticleDate');
const Byline = standaloneComponent<StandaloneBylineProps>('ContentByline');
const Image = standaloneComponent<StandaloneImageProps>('ContentImage');
const SaveArticleButton = standaloneComponent<StandaloneSaveArticleButtonProps>('ElementSaveArticleButton');
const Tag = standaloneComponent<StandaloneTagProps>('ElementTag');

const ElleStandaloneArticleHeader: StandaloneComponent<StandaloneArticleHeaderProps> = ({
  bylines,
  category,
  description,
  headline,
  image,
  tags,
  datetime,
  options,
  ...props
}) => {
  const {
    $bylines,
    $date,
    $category: categoryProps,
    $description,
    $headline,
    $image,
    $tags: tagsProps,
    ...$base
  } = options ?? {};

  const { $tag: $categoryTag, ...$category } = categoryProps ?? {};
  const { $tag, ...$tags } = tagsProps ?? {};

  const memoizedTags = useMemo(
    () =>
      tags?.map((tag, index) => (
        <Tag
          key={index}
          link={{ href: `/etikett/${tag.value}` }}
          {...mergeProps(
            {
              options: {
                variant: 'text',
                theme: componentTheme('tag'),
                ...$tag,
              },
            },
            tag,
          )}
        />
      )),
    [tags, $tag],
  );

  const memoCategoryTag = useMemo(
    () => (
      <Tag
        value={category?.value}
        link={{ href: category?.url || `etikett/${category?.value}` }}
        options={mergeOptions(
          {
            variant: 'text',
            theme: componentTheme('tag'),
          },
          $categoryTag,
        )}
      />
    ),
    [$categoryTag, category?.url, category?.value],
  );

  const stableImageProps = useStableProps({ fill: true, priority: true }, image);

  const stableProviderProps = useStable($base, { variant: 'third' });

  return (
    <ErrorBoundary>
      <ClassNameProvider value={stableProviderProps}>
        <ArticleHeader {...$base} {...props}>
          {tags && tags.length > 0 && <ArticleHeader.Tags {...$tags}>{memoizedTags}</ArticleHeader.Tags>}
          {headline && <ArticleHeader.Headline {...$headline}>{headline}</ArticleHeader.Headline>}
          {category?.value && <ArticleHeader.Category {...$category}>{memoCategoryTag}</ArticleHeader.Category>}
          {datetime?.publishedDatetime && (
            <ArticleHeader.Date {...$date}>
              <ArticleDate {...datetime} />
            </ArticleHeader.Date>
          )}
          {bylines && bylines?.length > 0 && (
            <ArticleHeader.Bylines {...$bylines}>
              {bylines?.map((byline, index) => <Byline {...byline} key={index} />)}
            </ArticleHeader.Bylines>
          )}
          {image && (
            <ArticleHeader.Image {...$image}>
              <Image {...stableImageProps} />
            </ArticleHeader.Image>
          )}
          {description && <ArticleHeader.Description {...$description}>{description}</ArticleHeader.Description>}
          <SaveArticleButton />
        </ArticleHeader>
      </ClassNameProvider>
    </ErrorBoundary>
  );
};

export const standalone = ElleStandaloneArticleHeader;

const ElleDefaultArticleHeader: DefaultComponent = ({ data, descendants }) => {
  const [{ device }] = useAppState();

  const { format = 'DD MMM, YYYY', publishedDatetime, updatedDatetime } = data ?? {};

  const labradorImage = descendants?.find((descendant) => descendant.type === 'labradorImage')?.data;

  const { imageUrl, alt, altText, byline } = labradorImage ?? {};

  const src = imageUrl?.[device];

  const bylines: StandaloneBylineProps[] = findBylines(descendants).map(({ data }) => ({
    type: BYLINE_TYPE.AUTHOR,
    author: [data.firstName, data.lastName].filter(Boolean).join(' '),
    authorPageSlug: data.authorPageSlug,
  }));

  if (data.photoByline) {
    bylines.push({
      type: BYLINE_TYPE.PHOTOGRAPHER,
      author: data.photoByline,
      authorPageSlug: data.authorPageSlug,
    });
  }

  if (data.extraBylineContent) {
    bylines.push({
      type: BYLINE_TYPE.FREE,
      author: data.extraBylineContent,
      text: data.extraBylineLabel,
      authorPageSlug: data.authorPageSlug,
    });
  }

  const stableProps = useStable({
    category: { value: data.category, url: data.categoryUrl },
    bylines,
    image: { src, alt: alt || altText, byline },
    datetime: { publishedDatetime, updatedDatetime, format },
    tags: data.tags?.map((tag: string) => ({
      value: tag,
    })),
  });

  return <ElleStandaloneArticleHeader headline={data.title} description={data.subtitle} {...stableProps} />;
};

export default ElleDefaultArticleHeader;
